import {DOCUMENT} from '@angular/common';
import {Component, HostListener, Inject, Input, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from '@ngneat/transloco';
import {debounceTime, distinctUntilChanged} from 'rxjs';
import {ORGANISER_EVENT_EDIT, DETAILS} from 'src/app/app-routing.module';
import {AbstractComponent, ConfirmationModalComponent, ValidationModalComponent} from "@components";
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  Lookups,
  PagedFilter,
  Pages, Paging,
  Request,
  RequestFilter,
  RequestStatus,
  RequestTypes
} from "@model";
import {AuthenticationService, EventService, ExportService, LoaderService, RequestService} from "@services";
import {environment} from "@environment";
import {ValidatorResult} from "../../../../../model/validation/ValidationError";

@Component({
  selector: 'app-list-of-requests',
  templateUrl: './list-of-requests.component.html',
  styleUrls: ['./list-of-requests.component.scss'],
})
export class ListOfRequestsComponent extends AbstractComponent implements OnInit, Paging<Request> {

  @Input('requestStatuses') statuses: Lookups[] = [];
  @Input() languages: Lookups[] = [];
  @Input('requestTypes') types: Lookups[] = [];
  @Input() requests: Request[] = [];

  showFabButton: boolean = false;
  selectedStatus: number | undefined;
  selectedType: number | undefined;
  isLoading: boolean = false;
  queryParamsFirstChange: boolean = true;
  hasMore: boolean = false;
  page: number = DEFAULT_PAGE_INDEX;
  pageFilter: number = 0;
  filter: PagedFilter<RequestFilter> | undefined;

  constructor(private route: ActivatedRoute, private router: Router, public translocoService: TranslocoService,
              private requestService: RequestService, private eventService: EventService,
              public authenticationService: AuthenticationService, private modalService: NgbModal,
              @Inject(DOCUMENT) private document: Document, private loaderService:LoaderService, private exportService: ExportService) {
    super();
  }

  ngOnInit(): void {
    this.handleExploreResult(this.requests);
    this.subscribeToRouteDataChange();
  }

  subscribeToRouteDataChange() {
    this.subscriptions.push(this.route.queryParams.pipe(debounceTime(environment.fullTextSearchDelay),
        distinctUntilChanged()).subscribe((queryParams: any) => {
        this.selectedType = this.isTypeValid(queryParams) ? +queryParams.type : undefined;
        this.selectedStatus = this.isStatusValid(queryParams) ? +queryParams.status : undefined;
        if (!this.queryParamsFirstChange && (queryParams.status === undefined || this.isStatusValid(queryParams)) && (queryParams.type === undefined || this.isTypeValid(queryParams))) {
          this.onExplore(false);
        }
        this.queryParamsFirstChange = false;
      })
    );
  }
  

  private isStatusValid(queryParams: any) {
    return Object.values(RequestStatus).filter((v: any) => !isNaN(Number(v))).map((v: any) => v.toString()).includes(queryParams.status)
  }

  private isTypeValid(queryParams: any) {
    return Object.values(RequestTypes).filter((v: any) => !isNaN(Number(v))).map((v: any) => v.toString()).includes(queryParams.type)
  }

  private changeQueryParams() {
    let queryParams: { [key: string]: any } = {};
    if (this.selectedStatus || this.selectedStatus === 0) {
      queryParams['status'] = this.selectedStatus;
    }
    if (this.selectedType || this.selectedType === 0) {
      queryParams['type'] = this.selectedType;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  @HostListener('window:scroll')
  toggleFabButton() {
    this.showFabButton = window.pageYOffset != 0;
  }

  actionsAllowed(request: Request) {
    return request.requestStatus === RequestStatus.ON_HOLD
  }

  scrollToTop(): void {
    return this.document.body.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start'
    });
  }

  private getFilter(): RequestFilter {
    let filter: RequestFilter = new RequestFilter();
    filter.status = this.selectedStatus ?? undefined;
    filter.type = this.selectedType ?? undefined;
    return filter;
  }

  private getPagedFilter(): void {
    let filter: RequestFilter = new RequestFilter();
    filter.status = this.selectedStatus ?? undefined;
    filter.type = this.selectedType ?? undefined;
    this.filter  = new PagedFilter(new Pages(undefined, this.pageFilter), filter);
  }

  handleExploreResult(value: Request[], loadMore?: boolean): void {
    if (value) {
      if (value.length > DEFAULT_PAGE_SIZE) {
        value = (value.slice(0, DEFAULT_PAGE_SIZE))
        this.hasMore = true;
      } else {
        this.hasMore = false;
      }
      if (loadMore && this.requests) {
        value = this.requests.concat(value)
      }
    }
    this.setListOfItems(value);
  }

  setListOfItems(value: Request[] | null) {
    if (value && value.length > 0) {
      this.requests = value;
      this.page++;
      this.pageFilter++;
    } else {
      this.requests = [];
    }
  }

  onExplore(loadMore?: boolean) {
    if (loadMore === true) {
      this.loaderService.startLoading();
    } else {
      this.page = 1;
      this.pageFilter= 0;
      this.isLoading = true;
    }
    this.requestService.filterRequests(new PagedFilter(new Pages(undefined, this.page), this.getFilter())).subscribe((value: Request[]) => {
      if (loadMore === true) {
        this.loaderService.dismissLoading();
      } else {
        setTimeout(() => {
            this.isLoading = false;
        }, 300);
      }
      this.handleExploreResult(value, loadMore);
    });
  }

  loadMore() :void {
    this.onExplore(true);
  }

  onStatusSelect(status: number | undefined) {
    this.selectedStatus = status;
    this.changeQueryParams()
  }

  onTypeSelect(type: number | undefined) {
    this.selectedType = type;
    this.changeQueryParams()
  }

  onModifyRequest(request: Request, accept: boolean): void {
    let copyRequest: Request = {...request};
    copyRequest.requestStatus = accept ? RequestStatus.ACCEPTED : RequestStatus.DENIED;
    if (copyRequest.type === RequestTypes.ORGANISER) {
      this.requestService.manageOrganiserRequest(copyRequest).subscribe((value: boolean) => {
        if (value) {
          this.requestService.fetchNewRequests.next(value);
          this.onExplore(false);
        }
      });
    } else if (copyRequest.type === RequestTypes.ADD_EVENT_TO_LEAGUE) {
      this.requestService.manageAddEventToLeagueRequest(copyRequest).subscribe((value: boolean) => {
        if (value) {
          this.requestService.fetchNewRequests.next(value);
          this.onExplore(false);
        }
      });
    } else if (copyRequest.type === RequestTypes.PUBLISH_EVENT) {
      this.requestService.manageEventRequest(copyRequest).subscribe((value: ValidatorResult | undefined) => {
        if (value) {
          if (value.hasErrors()) {
            const modalRef: NgbModalRef = this.modalService.open(ValidationModalComponent, {
              centered: true,
              size: 'lg'
            });
            modalRef.componentInstance.title = this.translocoService.translate("app.validation.error.event");
            modalRef.componentInstance.validatorResult = value;
            modalRef.componentInstance.showConfirmButton = true;
            modalRef.componentInstance.confirmButtonTitle = this.translocoService.translate("request.action.decline");
            modalRef.result.then((_) => {
              this.openModifyRequestDialog(request, false);
            }, (_) => {
              /// ignore
            });
          } else {
            this.requestService.fetchNewRequests.next(true);
            this.onExplore(false);
          }
        }
      });
    }
  }

  openRequestDetails(request: Request) {
    if (request.type === RequestTypes.ORGANISER) {
      this.router.navigate([DETAILS], {
        queryParams: {requestId: request.id}, relativeTo:this.route
      })
    } else if (request.type === RequestTypes.PUBLISH_EVENT) {
      if (request.event && request.event.id) {
        this.eventService.setCurrentEvent(request.event);
        this.router.navigate([ORGANISER_EVENT_EDIT], {queryParams: {eventId: request.event.id}});
      }
    }
  }

  getUserColumn(request: Request): String {
    return request.userDetail?.firstname + " " + request.userDetail?.lastname;
  }

  getOrganiserNameColumn(request: Request) {
    switch (request.type) {
      case RequestTypes.ORGANISER:
        return request.organiserRequest?.name ?? "";
      case RequestTypes.PUBLISH_EVENT:
        return request.event?.organiser?.name ?? "";
      default:
        return request.event?.organiser?.name ?? "";
    }
  }

  openModifyRequestDialog(request: Request, accept: boolean) {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    let requestComment: string = "";
    modalRef.componentInstance.title = this.translocoService.translate(accept ? "request.list.accept.dialog.title" : "request.list.decline.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate(accept ? "request.list.accept.dialog.text" : "request.list.decline.dialog.text");

    if (request.type === RequestTypes.ORGANISER || !accept) {
      modalRef.componentInstance.dispayTextArea = true;
      modalRef.componentInstance.textAreaPlaceholder = this.translocoService.translate("request.action.add.comment");
      modalRef.componentInstance.textAreInputEvent.subscribe((content: string) => requestComment = content);
    }

    modalRef.result.then((_) => {
      request.comment = requestComment !== "" ? requestComment : undefined;
      this.onModifyRequest(request, accept);
    }, (_) => {
      /// ignore
    });
  }
  exportRequestsList(type: string) {
    this.getPagedFilter();
    this.exportService. downloadRequests(type, this.filter).subscribe(
      (blob: Blob) => {
        const formattedDateTime = this.getCurrentDateTimeFormatted();
        const filename = type + '_' + formattedDateTime + '.' + type;
        const downloadLink = this.document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(blob);
        downloadLink.setAttribute('download', filename);
        this.document.body.appendChild(downloadLink);
        downloadLink.click();
      },
      error => {
        console.error('Error downloading file:', error);
      }
    );
  }
  getCurrentDateTimeFormatted(): string {
    const currentDateTime = new Date();
    const year = currentDateTime.getFullYear();
    const month = this.padNumber(currentDateTime.getMonth() + 1, 2);
    const day = this.padNumber(currentDateTime.getDate(), 2);
    return `${year}-${month}-${day}`;
  }

  padNumber(num: number, size: number): string {
    let numStr = num.toString();
    while (numStr.length < size) {
      numStr = '0' + numStr;
    }
    return numStr;
  }

  protected readonly RequestTypes = RequestTypes;
}



